import dayjs from 'dayjs';
import { action, computed, makeObservable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { NoteEntity, NoteEntityApi, noteSchema } from '@/entities/Notes';
import type { NotificationsStore } from '@/stores/global/RootStore/NotificationsStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { SequentialFieldPrefillStore } from '@/stores/local/shared/SequentialFieldPrefillStore';
import { FormCreateCaptionTextStore } from '@/stores/local/shared/form/FormCreateCaptionTextStore';
import call from '@/utils/api/api';
import { schemeParse } from '@/utils/schemeParse';

type NoteQuickCaptureStoreParams = {
  organizationId: string;
  notificationsStore: NotificationsStore;
};

type NoteQuickCaptureCreatePayload = {
  artifact_type: 'document' | 'analysis' | 'news' | 'manual';
  artifact_id: string | null;
  anchor: {
    type?: string;
    document_page?: number;
    quote?: string | null;
    rects?: unknown[] | null;
    point?: Record<string, unknown> | null;
    path?: string | null;
    analysis_type_id?: number;
  } | null;
};

export class NoteQuickCaptureStore extends PopupStore<null | string> {
  private readonly _organizationId: string;
  private readonly _notificationsStore: NotificationsStore;
  private _createPayloadOverride: Partial<NoteQuickCaptureCreatePayload> | null = null;
  readonly form = new FormCreateCaptionTextStore<NoteEntity>({
    captionDefaultValue: 'Новая заметка',
  });

  readonly meta = new MetaStore();

  private readonly _captionPrefill: SequentialFieldPrefillStore;

  constructor({ organizationId, notificationsStore }: NoteQuickCaptureStoreParams) {
    super();
    this._organizationId = organizationId;
    this._notificationsStore = notificationsStore;
    this._captionPrefill = new SequentialFieldPrefillStore({
      title: 'Заметка',
      field: this.form.fieldsMap.caption,
      valueFactory: ({ title }) => `${title} ${dayjs().format('DD.MM.YYYY HH:mm')}`,
    });

    makeObservable<NoteQuickCaptureStore>(this, {
      canSubmit: computed,
      isLoading: computed,
      openWithSelection: action.bound,
      submit: action.bound,
    });
  }

  get canSubmit(): boolean {
    return !this.meta.isLoading && !this.form.isSubmitDisabled;
  }

  get isLoading(): boolean {
    return this._captionPrefill.meta.isLoading;
  }

  private _getDefaultCreatePayload(): NoteQuickCaptureCreatePayload {
    return {
      artifact_type: 'manual',
      artifact_id: null,
      anchor: null,
    };
  }

  openWithSelection(selection: string, options?: { createPayload?: Partial<NoteQuickCaptureCreatePayload> }) {
    this.meta.reset();
    this.form.reset();
    this._captionPrefill.reset();
    this._createPayloadOverride = options?.createPayload ?? null;

    const normalizedSelection = selection.trim();

    if (normalizedSelection.length > 0) {
      this.form.fieldsMap.text.setValue(normalizedSelection, { noTouch: true });
      this.form.fieldsMap.text.unTouch();
    }

    this._captionPrefill.ensure();
    super.open();
  }

  close() {
    super.close();
    this.meta.reset();
    this.form.reset();
    this._captionPrefill.reset();
    this._createPayloadOverride = null;
  }

  async submit() {
    if (this.meta.isLoading) {
      return;
    }

    if (!this.form.validateFields()) {
      return;
    }

    this.meta.setLoadedStartMeta();
    const payload = {
      ...this._getDefaultCreatePayload(),
      ...(this._createPayloadOverride ?? {}),
    };

    const response = await call<NoteEntityApi>({
      endpoint: ENDPOINTS.note.create(this._organizationId),
      method: 'POST',
      data: {
        ...this.form.serverFormat,
        ...payload,
      },
    });

    if (response.isError) {
      this._notificationsStore.showError(
        response.error.response?.data.message ?? 'Не удалось создать заметку. Попробуйте еще раз',
      );
      this.meta.setLoadedErrorMeta();

      return;
    }

    const parsed = schemeParse(noteSchema, response.data);

    if (!parsed.success) {
      this._notificationsStore.showError('Не удалось создать заметку. Попробуйте еще раз');
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      this.meta.setLoadedSuccessMeta();
      this.close();
    });

    this._notificationsStore.showSuccess('Заметка создана');
  }

  destroy(): void {
    this.form.destroy();
    this.meta.destroy();
    this._captionPrefill.destroy();
  }
}
